// @flow
/**
 * Given a bitmap image URL, return the base64-encoded binary string for that image
 * @param  {string} url URL of a bitmap image
 * @return {string}     Base64-encoded representation of the image
 */
function getAndEncodeImage(url: string): Promise<string> {
  const TIMEOUT = 30000;
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.ontimeout = reject;
    xhr.onreadystatechange = () => {
      if (xhr.readyState !== 4) {
        return;
      }
      if (xhr.status !== 200) {
        reject();
        return;
      }
      const encoder = new FileReader();
      encoder.onloadend = () => {
        const result = encoder.result;
        if (typeof result === 'string') {
          resolve(result.split(/,/)[1]);
        } else {
          reject();
        }
      };
      encoder.readAsDataURL(xhr.response);
    };
    xhr.open('GET', url, true);
    // NOTE: timeout and repsonseType have to be set after `xhr.open`
    xhr.timeout = TIMEOUT;
    xhr.responseType = 'blob';
    xhr.send();
  });
}

/**
 * Given a bitmap image URL, return the MIME type based on its file extension
 * @param  {string} url URL of a bitmap image
 * @return {string}     MIME type of the image
 */
function getMimeTypeByFileExtension(url: string): string {
  const matches = url.match(/\.([^\.\/]*?)$/);
  switch (matches && matches[1]) {
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'gif':
      return 'image/gif';
    default:
      return '';
  }
}

type NormalizeURL = (string) => string;

/**
 * Inline the bitmap image for the <image> SVG element
 * @param  {SVGImageElement} element an <image> SVG element whose `xlink:href` could be
 *                                   either inlined data URI or an URL for a bitmap image.
 * @param  {(string)=>string} normalizeURL normalize bitmap image URL (e.g. to higher resolution)
 * @return {Promise}  a promise that is resolved after inlining the bitmap image as
 *                      base64-encoded data URI in its `xlink:href`
 */
function inlineSvgImage(element: window.SVGImageElement, normalizeURL: ?NormalizeURL) {
  const url = element.href.baseVal;
  if (url.startsWith('data:')) {
    return Promise.resolve();
  }
  const normalizedURL = normalizeURL ? normalizeURL(url) : url;
  return Promise.resolve(normalizedURL)
    .then(getAndEncodeImage)
    .then((data) => `data:${getMimeTypeByFileExtension(normalizedURL)};base64,${data}`)
    .then(
      (dataUrl) =>
        new Promise((resolve, reject) => {
          element.onerror = reject;
          element.href.baseVal = dataUrl;
          window.requestAnimationFrame(resolve);
        }),
    );
}

/**
 * Recursively inline the bitmap image of all <image> SVG elements
 * @param  {Node} node the root node to start recursion from
 * @param  {(string)=>string} normalizeURL normalize bitmap image URL (e.g. to higher resolution)
 * @return {Promise} a promise that is resolved after ensuring that every bitmap image
 *                     in the tree has been inlined as a base64-encoded data URI
 */
function inlineSvgImages(node: Node, normalizeURL: ?NormalizeURL): Promise<any> {
  if (!(node instanceof Element)) {
    return Promise.resolve(node);
  }
  if (node instanceof window.SVGImageElement) {
    return inlineSvgImage(node, normalizeURL);
  }
  const childNodeArray = Array.from(node.childNodes); // convert NodeList to Array for iteration
  return Promise.all(childNodeArray.map((child) => inlineSvgImages(child, normalizeURL)));
}

/**
 * Convert a <svg> into a <img>. Note that this function also makes sure that all bitmap images
 * in the <svg> are inlined with base64-encoding such that they can be drawn to a canvas.
 * @param  {HTMLElement} svgParent The parent node of the SVG element node
 * @param  {(string)=>string} normalizeURL normalize bitmap image URL (e.g. to higher resolution)
 * @return {Promise<Image>}       A promise which will resolve with a HTML Image element
 *                                  after the image of the image element is embedded/inlined.
 */
export function svgParentToImage(
  svgParent: HTMLElement,
  normalizeURL: ?NormalizeURL,
): Promise<Image> {
  return new Promise((resolve, reject) => {
    const clonedParent = document.createElement('div');
    clonedParent.innerHTML = svgParent.innerHTML; // clone because we will have to manipulate it

    // NOTE: we have to inline all external bitmap images as base64-encoded data URI so that
    // they can be drawn to a canvas later
    inlineSvgImages(clonedParent, normalizeURL).then(() => {
      const image = new Image();
      image.onload = () => {
        window.requestAnimationFrame(() => resolve(image)); // needed on Safari
      };
      image.onerror = reject;
      const svgNode = clonedParent.querySelector('svg');
      if (svgNode) {
        // NOTE: Firefox requires width and height to be a number.
        // See https://stackoverflow.com/a/28692538/4056191
        if (!/^[0-9]+$/.test(svgNode.getAttribute('width') || '')) {
          svgNode.setAttribute('width', `${svgParent.scrollWidth}`);
        }
        if (!/^[0-9]+$/.test(svgNode.getAttribute('height') || '')) {
          svgNode.setAttribute('height', `${svgParent.scrollHeight}`);
        }
      }
      const html = clonedParent.innerHTML.replace(/(xmlns="[^"]+"[^>]*)\ xmlns="[^"]+"/, '$1'); // NOTE: remove duplicated xmlns for IE
      image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(html.trim())}`;
    });
  });
}

/**
 * Returns a promise that converts a <canvas> to a Blob object
 * See https://developer.mozilla.org/en-US/docs/Web/API/HTMLCanvasElement/toBlob
 * @param  {HTMLCanvasElement} canvas
 * @return {Blob} a promise which will resolve with a Blob object representing the image
 *                  contained in the canvas
 */
function canvasToBlob(canvas: window.HTMLCanvasElement) {
  return new Promise((resolve, reject) => {
    if (canvas.toBlob) {
      canvas.toBlob(resolve);
      return;
    }
    let dataURL = '';
    try {
      dataURL = canvas.toDataURL('image/png');
    } catch (e) {
      // Canvas.toDataURL does not work on IE 11. A security error is thrown.
      // See https://connect.microsoft.com/IE/feedback/details/1092671/
      reject();
    }
    const binaryString = window.atob(dataURL.split(',')[1]);
    const length = binaryString.length;
    const binaryArray = new Uint8Array(length);
    for (let i = 0; i < length; i += 1) {
      binaryArray[i] = binaryString.charCodeAt(i);
    }
    resolve(
      new window.Blob([binaryArray], {
        type: 'image/png',
      }),
    );
  });
}

/**
 * Save the image contained in the given <canvas> as a PNG file
 * @param  {HTMLCanvasElement}  canvas
 * @param  {string}             fileName
 * @return {Promise}            a promise which will resolve after download event is triggered
 */
export function saveCanvasAsPng(canvas: window.HTMLCanvasElement, fileName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    // NOTE: There are 2 possible formats of embedding the image data:
    // 1. data URL (data:...)
    //    See https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/Data_URIs
    // 2. blob/object URL (blob:...)
    //    See https://developer.mozilla.org/en-US/docs/Web/API/URL/createObjectURL
    //
    // We have to choose the blob URL format here because of the size limit in data URL.
    // See https://stackoverflow.com/a/41755526/4056191 for more details.
    canvasToBlob(canvas)
      .then((blob) => window.URL.createObjectURL(blob))
      .then((blobURL: string) => {
        const link = document.createElement('a');
        link.download = fileName;
        link.href = blobURL;
        link.style.display = 'none';
        window.document.body.appendChild(link); // needed on Firefox
        window.requestAnimationFrame(() => {
          // wait for a frame
          const clickHandler = () => {
            window.requestAnimationFrame(() => {
              // wait for a frame
              window.URL.revokeObjectURL(blobURL);
              link.removeAttribute('href');
              link.removeEventListener('click', clickHandler);
              window.document.body.removeChild(link);
              resolve();
            });
          };
          link.addEventListener('click', clickHandler);
          link.click(); // this triggers download
        });
      })
      .catch(reject);
  });
}
